// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback, useEffect } from 'react';
import { getSignatoryStatusSummary } from '../api/signatoryStatusApi';
import type { SignatoryStatusSummaryDTO } from '../types/signatoryStatus';

interface UseSignatoryStatusSummaryReturn {
  readonly data: SignatoryStatusSummaryDTO | null;
  readonly loading: boolean;
  readonly error: string | null;
  readonly refresh: () => Promise<void>;
}

export function useSignatoryStatusSummary(profileId: string, enabled = true): UseSignatoryStatusSummaryReturn {
  const [data, setData] = useState<SignatoryStatusSummaryDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled || !profileId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getSignatoryStatusSummary(profileId);
      setData(result);
    } catch (err: unknown) {
      console.error('Failed to fetch signatory status summary:', err);
      setError('Failed to load signatory status summary');
    } finally {
      setLoading(false);
    }
  }, [profileId, enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
